/* eslint-disable @typescript-eslint/no-explicit-any */
import type { APIMedia, APIMediaCollection } from "~~/shared/types/media";

// ---------- Types ----------
type NuxtImgProps = {
    src: string;
    width?: number | string;
    height?: number | string;
    alt?: string;
    sizes?: string;
    densities?: string;
    placeholder?: string | boolean;
    placeholderClass?: string; // <NuxtImg placeholder-class>, camelCased here
    provider?: string;
    preset?: string;
    format?: string;
    quality?: number;
    fit?: string; // e.g. 'cover' | 'contain' (provider-dependent)
    modifiers?: Record<string, any>;
    preload?: boolean;
    loading?: "lazy" | "eager";
    nonce?: string;
    class?: string;
};

type GetImageState = { kind: "single"; img: NuxtImgProps } | { kind: "responsive"; desktop: NuxtImgProps; mobile: NuxtImgProps };

type Input = string | APIMedia | APIMediaCollection | APIMediaCollection[] | null | undefined;

type NuxtPropsInput =
    | Partial<NuxtImgProps>
    | {
          desktop?: Partial<NuxtImgProps>;
          mobile?: Partial<NuxtImgProps>;
      };

// ---------- Narrowing ----------
const isAPIMedia = (value: unknown): value is APIMedia => {
    return (
        !!value &&
        typeof value === "object" &&
        ("original_url" in (value as Record<string, unknown>) ||
            "preview_url" in (value as Record<string, unknown>) ||
            "file_name" in (value as Record<string, unknown>))
    );
};

// ---------- Defaults (project-level, NOT Nuxt's) ----------
const DEFAULT_IMAGE = "/images/no-image.jpg";
const DEFAULT_CLASS = "object-cover";
const DEFAULTS: Required<Pick<NuxtImgProps, "loading" | "class">> & Partial<NuxtImgProps> = {
    loading: "lazy",
    class: DEFAULT_CLASS,
    // We set the remaining props to neutral defaults so that Nuxt's behavior is not altered
    // (format/quality/fit/sizes/... are only set when the user provides them)
};

// ---------- Resolvers ----------
const resolveFromMedia = (media: APIMedia | null | undefined): NuxtImgProps => {
    if (!media) return { src: DEFAULT_IMAGE, alt: "", class: DEFAULT_CLASS };
    const src = (media.original_url || media.preview_url || DEFAULT_IMAGE) as string;
    const alt = (media.name || media.file_name || "") as string;
    return { src, alt, class: DEFAULT_CLASS };
};

const resolveFromCollection = (collection: APIMediaCollection | APIMediaCollection[] | null | undefined): NuxtImgProps => {
    if (!collection) return { src: DEFAULT_IMAGE, alt: "", class: DEFAULT_CLASS };

    if (Array.isArray(collection)) {
        for (const item of collection) {
            if (!item || typeof item !== "object") continue;
            const vals = Object.values(item as Record<string, unknown>);
            for (const val of vals) {
                if (isAPIMedia(val)) return resolveFromMedia(val);
            }
        }
        return { src: DEFAULT_IMAGE, alt: "", class: DEFAULT_CLASS };
    }

    const values = Object.values(collection as Record<string, unknown>);
    for (const val of values) {
        if (isAPIMedia(val)) return resolveFromMedia(val);
    }

    return { src: DEFAULT_IMAGE, alt: "", class: DEFAULT_CLASS };
};

function toImgBind(input?: Input): NuxtImgProps {
    if (!input) return { src: DEFAULT_IMAGE, alt: "", class: DEFAULT_CLASS };

    if (typeof input === "string") {
        return { src: input, alt: "", class: DEFAULT_CLASS };
    }

    if (isAPIMedia(input)) return resolveFromMedia(input);

    return resolveFromCollection(input as APIMediaCollection | APIMediaCollection[]);
}

// ---------- Utils ----------
function normalizeProps(p?: Partial<NuxtImgProps>): Partial<NuxtImgProps> {
    if (!p) return {};
    const out: Partial<NuxtImgProps> = { ...p } as any;

    // allow dash-case provided by callers and normalize to camelCase
    // @ts-expect-error runtime normalization
    if (p["placeholder-class"] && !p.placeholderClass) {
        // @ts-expect-error reading string index
        out.placeholderClass = p["placeholder-class"] as any;
        delete (out as any)["placeholder-class"];
    }

    return out;
}

function applyDefaults(base: NuxtImgProps, overrides?: Partial<NuxtImgProps>): NuxtImgProps {
    const o = normalizeProps(overrides);
    // merge order: DEFAULTS → base(bind) → overrides
    return { ...DEFAULTS, ...base, ...o };
}

// ---------- Public API ----------
/**
 * Build NuxtImg-friendly props from APIMedia or collections.
 * - desktop: first input (required)
 * - mobile: second input (optional for mobile art-direction)
 * - props: NuxtImg property object:
 *    1) a single object => applied to both (or single image)
 *    2) separate object { desktop?, mobile? } => applied separately
 */
export default function getImage(desktop?: Input, mobile?: Input, props?: NuxtPropsInput): GetImageState {
    const dBase = toImgBind(desktop);
    const mBase = mobile ? toImgBind(mobile) : null;

    const unified = props && !("desktop" in props || "mobile" in props) ? normalizeProps(props as Partial<NuxtImgProps>) : undefined;
    const desktopOverrides = props && "desktop" in (props as any) ? normalizeProps((props as any).desktop) : unified;
    const mobileOverrides = props && "mobile" in (props as any) ? normalizeProps((props as any).mobile) : unified;

    if (!mBase || !mBase.src || mBase.src === dBase.src) {
        return { kind: "single", img: applyDefaults(dBase, desktopOverrides) };
    }

    return {
        kind: "responsive",
        desktop: applyDefaults(dBase, desktopOverrides),
        mobile: applyDefaults(mBase, mobileOverrides),
    };
}

export function getSingleImage(desktop?: Input, props?: NuxtPropsInput) {
    const res = getImage(desktop, undefined, props);
    if (res.kind === "single") return res.img;
    return null;
}
